// Initial state used for testing
// export const INITIAL_STATE = {
//   money: 100000000,
//   adFrequency: 0,
//   adPersonalization: 0,
//   algorithmLevel: 1,
//   maxAlgorithmLevel: 10,
//   userCount: 1000000,
//   reputation: 100,
//   engagement: 1,

//   // Tutorial and UI state flags
//   isLaunched: true,
//   tutorialStarted: true,
//   showMetrics: true,
//   showControls: true,
//   firstGoalGiven: true,
//   showAlgorithmUpgrade: true,
//   shownAlgorithmMessage: true,
//   showAdPersonalization: true,
//   tutorialComplete: true,
//   shownInvestmentOffer: true,

//   // Company info
//   companyName: "test",
// };

export const INITIAL_STATE = {
  money: 600,
  adFrequency: 0,
  adPersonalization: 0,
  algorithmLevel: 1,
  maxAlgorithmLevel: 1,
  userCount: 1000,
  reputation: 50,
  engagement: 1,

  // Tutorial and UI state flags
  isLaunched: false,
  tutorialStarted: false,
  showMetrics: false,
  showControls: false,
  firstGoalGiven: false,
  showAlgorithmUpgrade: false,
  shownAlgorithmMessage: false,
  showAdPersonalization: false,
  tutorialComplete: false,
  shownInvestmentOffer: false,
  showGoals: false,

  // Company info
  companyName: "",
};

export const GAME_CONSTANTS = {
  // Revenue and cost constants
  REVENUE_PER_ENGAGED_USER: 0.8,
  BASE_OVERHEAD_COST: 0.015,
  AD_REPUTATION_PENALTY: 0.3,

  // Algorithm constants
  ALGORITHM_BASE_COST: 1000,
  BASE_ALGORITHM_MULTIPLIER: 1.2, // Starting multiplier
  MULTIPLIER_INCREMENT: 0.15, // How much the multiplier increases each level
  MAX_ALGORITHM_MULTIPLIER: 2.5, // Cap on the multiplier
  EFFICIENCY_GAIN_PER_LEVEL: 0.15, // How much more efficient lower levels become
  MAX_EFFICIENCY_GAIN: 0.75, // Maximum efficiency improvement (75% cost reduction)
  ALGORITHM_REPUTATION_BONUS: 4,

  // User engagement constants
  MIN_ENGAGEMENT: 0.15,
  AD_FREQUENCY_ENGAGEMENT_PENALTY: 20,
  MAX_USER_LOSS_RATE: 0.35,

  // Other constants remain the same
  BASE_REPUTATION: 50,
  REPUTATION_TO_GROWTH_MULTIPLIER: 120,
  GROWTH_RATE_DIVISOR: 12,
  REPUTATION_CLIFF_THRESHOLD: 90,
  ENGAGEMENT_DECAY_RATE: 0.15,
  TICK_RATE_MS: 1000,
  AD_PERSONALIZATION_MULTIPLIER: 5,
  BASE_AD_REVENUE_MULTIPLIER: 0.3,
  PERSONALIZED_AD_BONUS: 2,
};

export const GAME_FORMULAS = {
  calculateAlgorithmCost: (level) => {
    // Calculate dynamic multiplier that increases with each level
    const dynamicMultiplier = Math.min(
      GAME_CONSTANTS.BASE_ALGORITHM_MULTIPLIER +
        (level - 1) * GAME_CONSTANTS.MULTIPLIER_INCREMENT,
      GAME_CONSTANTS.MAX_ALGORITHM_MULTIPLIER
    );

    return Math.floor(
      GAME_CONSTANTS.ALGORITHM_BASE_COST *
        Math.pow(dynamicMultiplier, level - 1)
    );
  },

  calculateOverheadCost: (
    userCount,
    engagement,
    algorithmLevel,
    maxAlgorithmLevel
  ) => {
    // Base overhead for current level
    const baseOverhead =
      userCount *
      engagement *
      Math.pow(algorithmLevel, 1.5) *
      GAME_CONSTANTS.BASE_OVERHEAD_COST;

    // Calculate efficiency gain for each level based on max level
    const efficiencyGain = Math.min(
      (maxAlgorithmLevel - algorithmLevel) *
        GAME_CONSTANTS.EFFICIENCY_GAIN_PER_LEVEL,
      GAME_CONSTANTS.MAX_EFFICIENCY_GAIN
    );

    // Apply efficiency reduction
    const finalOverhead = baseOverhead * (1 - efficiencyGain);

    return Math.floor(finalOverhead);
  },

  // Other formulas remain the same...
  calculateRevenue: (userCount, engagement, adFrequency, adPersonalization) => {
    const baseRevenue =
      userCount *
      engagement *
      (adFrequency / 100) *
      GAME_CONSTANTS.BASE_AD_REVENUE_MULTIPLIER;

    const personalizationBonus =
      Math.sqrt(adPersonalization / 100) * GAME_CONSTANTS.PERSONALIZED_AD_BONUS;

    return Math.floor(
      baseRevenue *
        (1 + personalizationBonus) *
        GAME_CONSTANTS.REVENUE_PER_ENGAGED_USER
    );
  },

  calculateEngagement: (algorithmLevel, adFrequency, userCount) => {
    const baseEngagement = 0.8;
    const algorithmBoost = Math.pow(1.4, algorithmLevel / 2);
    const complexityDecay = Math.max(
      0,
      (algorithmLevel * Math.log10(Math.max(userCount, 1000))) / 200
    );

    const purityPenalty = adFrequency > 0 ? 0.2 : 0;
    const linearPenalty = (adFrequency / 100) * 0.4;
    const adDecayThreshold = 25;
    const thresholdPenalty =
      adFrequency <= adDecayThreshold
        ? 0
        : 0.4 * Math.pow((adFrequency - adDecayThreshold) / 75, 2);

    const totalAdPenalty = purityPenalty + linearPenalty + thresholdPenalty;

    return Math.max(
      GAME_CONSTANTS.MIN_ENGAGEMENT,
      (baseEngagement * algorithmBoost - complexityDecay) * (1 - totalAdPenalty)
    );
  },

  calculateReputation: (algorithmLevel, adPersonalization, adFrequency) => {
    if (adFrequency >= 100 || adPersonalization >= 100) {
      return 0;
    }

    const algorithmBonus =
      algorithmLevel * GAME_CONSTANTS.ALGORITHM_REPUTATION_BONUS;

    const personalizedIntensity = Math.max(0, adPersonalization - 50) / 50;
    const adIntensity = (adFrequency * personalizedIntensity) / 100;

    const baseAdPenalty =
      adFrequency === 0 ? -20 : Math.pow(adFrequency / 50, 1.5) * 30;

    const personalizationPenalty = Math.pow(adIntensity * 100, 1.5);

    const approachingMaxPenalty = Math.max(
      Math.pow(adFrequency / GAME_CONSTANTS.REPUTATION_CLIFF_THRESHOLD, 4),
      adPersonalization > 50
        ? Math.pow((adPersonalization - 50) / 50, 3) * 30
        : 0
    );

    return Math.min(
      100,
      Math.max(
        0,
        GAME_CONSTANTS.BASE_REPUTATION +
          algorithmBonus -
          baseAdPenalty -
          personalizationPenalty -
          approachingMaxPenalty
      )
    );
  },

  calculateUserGrowth: (userCount, reputation) => {
    const reputationDiff = reputation - GAME_CONSTANTS.BASE_REPUTATION;

    const growthRate = reputation === 0 ? -4 : (reputationDiff / 100) * 0.5;

    const growth = userCount * growthRate;

    return growth / GAME_CONSTANTS.GROWTH_RATE_DIVISOR;
  },
};

export class GameState {
  constructor() {
    this.state = {
      ...INITIAL_STATE,
      maxAlgorithmLevel: 1, // Track the highest level unlocked
    };
    this.listeners = new Set();
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    this.listeners.forEach((listener) => listener(this.state));
  }

  getState() {
    return { ...this.state };
  }

  setState(newState) {
    // Preserve all UI and tutorial flags
    const preservedFlags = {
      isLaunched: this.state.isLaunched,
      tutorialStarted: this.state.tutorialStarted,
      showMetrics: this.state.showMetrics,
      showControls: this.state.showControls,
      firstGoalGiven: this.state.firstGoalGiven,
      showAlgorithmUpgrade: this.state.showAlgorithmUpgrade,
      shownAlgorithmMessage: this.state.shownAlgorithmMessage,
      showAdPersonalization: this.state.showAdPersonalization,
      tutorialComplete: this.state.tutorialComplete,
      companyName: this.state.companyName,
    };

    // Only update flags if explicitly provided in newState
    const updatedFlags = {};
    for (const key of Object.keys(preservedFlags)) {
      updatedFlags[key] =
        newState[key] !== undefined ? newState[key] : preservedFlags[key];
    }

    this.state = {
      ...this.state,
      ...newState,
      ...updatedFlags,
    };

    this.notifyListeners();
  }

  setTutorialFlag(flag, value) {
    this.setState({ [flag]: value });
  }

  updateTutorialState(updates) {
    this.setState(updates);
  }

  changeAdFrequency(delta) {
    const newFrequency = Math.min(
      100,
      Math.max(0, this.state.adFrequency + delta)
    );
    this.setState({ adFrequency: newFrequency });
  }

  changeAdPersonalization(delta) {
    const maxPersonalization =
      this.state.algorithmLevel * GAME_CONSTANTS.AD_PERSONALIZATION_MULTIPLIER;
    const newPersonalization = Math.min(
      maxPersonalization,
      Math.max(0, this.state.adPersonalization + delta)
    );
    this.setState({ adPersonalization: newPersonalization });
  }

  changeAlgorithmLevel(delta) {
    const newLevel = Math.min(
      this.state.maxAlgorithmLevel,
      Math.max(1, this.state.algorithmLevel + delta)
    );
    if (newLevel !== this.state.algorithmLevel) {
      this.setState({ algorithmLevel: newLevel });
    }
    if (!this.state.showAdPersonalization) {
      this.changeAdPersonalization(100); // Set it to max if not already shown
    }
  }

  upgradeAlgorithm() {
    const nextLevel = this.state.maxAlgorithmLevel + 1;
    const cost = GAME_FORMULAS.calculateAlgorithmCost(
      this.state.maxAlgorithmLevel
    );
    if (this.state.money >= cost) {
      this.setState({
        money: this.state.money - cost,
        maxAlgorithmLevel: nextLevel,
      });
    }
    if (this.state.algorithmLevel === this.state.maxAlgorithmLevel - 1) {
      this.changeAlgorithmLevel(1);
    }
  }

  update() {
    if (this.state.gameOver) return;

    const {
      userCount,
      adFrequency,
      adPersonalization,
      algorithmLevel,
      engagement,
      maxAlgorithmLevel,
    } = this.state;

    // Check win condition
    if (
      this.state.money >= 1000000 &&
      this.state.userCount >= 100000 &&
      this.state.maxAlgorithmLevel >= 10
    ) {
      this.setState({
        gameOver: true,
        gameOverReason: "win",
      });
      return;
    }

    // Check loss conditions
    if (this.state.money < -userCount) {
      this.setState({
        ...this.state,
        gameOver: true,
        gameOverReason: "deficit",
      });
      return;
    }

    if (userCount <= 0) {
      this.setState({
        ...this.state,
        gameOver: true,
        gameOverReason: "users",
      });
      return;
    }

    // Continue with normal game updates

    // Check loss condition: deficit exceeds user count
    if (this.state.money < -userCount) {
      this.setState({
        gameOver: true,
        gameOverReason: "deficit",
        gameOverMessage:
          "I'm sorry, but your deficit has exceeded your user count. I have to pull my investment.",
      });
      return;
    }

    // Check loss condition: no users left
    if (userCount <= 1) {
      console.log("Game over: no users left");
      this.setState({
        gameOver: true,
        gameOverReason: "users",
        gameOverMessage:
          "With no users left on the platform, there's nothing more we can do here.",
      });
      return;
    }

    const newEngagement = GAME_FORMULAS.calculateEngagement(
      algorithmLevel,
      adFrequency,
      userCount
    );

    const newReputation = GAME_FORMULAS.calculateReputation(
      algorithmLevel,
      adPersonalization,
      adFrequency
    );
    const userGrowth = GAME_FORMULAS.calculateUserGrowth(
      userCount,
      newReputation
    );
    const revenue = GAME_FORMULAS.calculateRevenue(
      userCount,
      engagement,
      adFrequency,
      adPersonalization
    );
    const overheadCost = GAME_FORMULAS.calculateOverheadCost(
      userCount,
      engagement,
      algorithmLevel,
      maxAlgorithmLevel
    );

    this.setState({
      engagement: newEngagement,
      reputation: newReputation,
      userCount: Math.max(0, userCount + userGrowth),
      money: this.state.money + revenue - overheadCost,
    });
  }
}
